import { createColumnHelper } from "@tanstack/react-table";
import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import showNotification from "../../../utils/showNotification";
import { ERROR_HANDLER } from "../../../utils/error-handler";
import { Badge, Flex, Loader, Tooltip } from "@mantine/core";
import AuthContext from "../../../contexts/AuthContext/AuthContext";
import moment from "moment";
import filterFunctions from "../../../utils/filterFunctions";
import { searchFlattenedObject } from "../../../utils/searchFlattenedObject";
import { currencyFormat } from "../../../utils/currencyFormat";
import { IconEye, IconSquareCheck } from "@tabler/icons-react";
import AppContext from "../../../contexts/AppContext";
import useS3 from "../../useS3";
import { isBase64 } from "../../../utils/isBase64";




const getStateBadge = (state) => {
   let text, color;
   switch (state) {
      case "PAID":
         text = "Pagado";
         color = "rgb(158, 192, 152)"; // var(--color-badge-green)
         break;
      case "PENDING":
         text = "Pendiente";
         color = "rgb(197,189,132)"; // var(--color-badge-yellow)
         break;
      case "EXPIRED":
         text = "Vencida";
         color = "rgb(150,61,61)"; // var(--color-badge-yellow)
         break;
      case "CANCELLED":
         text = "Cancelada";
         color = "rgb(150,61,61)";
         break;
      default:
         text = "Error";
         color = "rgb(150,61,61)"; // var(--color-badge-red)
         break;
   }
   return <div className="salesPayment__badge__container">
      <Badge color={color}>{text}</Badge>
   </div>
}

const chargeTypes = {
   'manual': ['TRANSFERENCIA'],
   'automatic': ['PAYPAL', 'PAGOS360'],
}

const getChargeType = (type) => {
   if (chargeTypes.automatic.includes(type.toUpperCase())) return 'AUTOMÁTICO';
   // Add more types here
   return 'MANUAL';
}


export default function useSalesInstallments() {
   const { authToken } = useContext(AuthContext)
   const { dollarValue, bankAccountsOptions } = useContext(AppContext);
   const { uploadToS3 } = useS3();
   const [data, setData] = useState()
   const [isLoading, setIsLoading] = useState(false)
   const [isSwitchLoading, setIsSwitchLoading] = useState(null)
   const [reFetch, setReFetch] = useState(false)
   const [studentData, setStudentData] = useState(null);
   const [displayForm, setDisplayForm] = useState(false);
   const [initialValuesEdit, setInitialValuesEdit] = useState(null);
   const [confirmModalData, setIsConfirmModalData] = useState(null);
   const [detailsModal, setDetailsModal] = useState(null);
   const [isCancelling, setIsCancelling] = useState(false);


   const firstDayOfThisMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
   const currentDay = new Date();

   const [dateFilter, setDateFilter] = useState([firstDayOfThisMonth, currentDay]);


   const axiosConfig = {
      headers: {
         "Content-Type": "application/json",
         Authorization: `Bearer ${authToken}`,
      },
   }

   async function getSalesInstallments() {
      try {
         setIsLoading(true)
         const getURL = `${process.env.REACT_APP_BACKEND_SERVER}admin/administracion/installmentTable?initDate=${dateFilter[0]}&endDate=${dateFilter[1]}`
         // const getURL = `${process.env.REACT_APP_BACKEND_SERVER}admin/administracion/installmentTable`
         const { data: response } = await axios.get(getURL, axiosConfig)

         const mappedResponse = response.map((item) => {
            const isPaid = getIfPayed(item.installments_state);
            const dateToCompare = new Date(item.installments_date);

            // if (!isPaid && dateToCompare < currentDay) {
            //    item.installments_state = 'EXPIRED'
            // }
            if (!isPaid && item.installments_state !== 'CANCELLED' && dateToCompare < currentDay) {
               item.installments_state = 'EXPIRED'
            }
            return item
         });

         // Sort by date
         mappedResponse.sort((a, b) => {
            return new Date(a.installments_date) - new Date(b.installments_date)
         })
         setData(mappedResponse)

      } catch (error) {
         showNotification({
            color: "red",
            status: "error",
            title: "Operación fallida.",
            message:
               `No se pudo obtener los cobros. 
            Detalle: ${error.message || ERROR_HANDLER[error.request?.status] || ERROR_HANDLER.defaultError}`,
            autoClose: 12000,
         });
      } finally {
         setIsLoading(false)
      }
   }

   const cancelPayment = async ({ payment_id, reason }) => {
      if (!payment_id) {
         showNotification({
            color: "red",
            status: "error",
            title: "Operación fallida.",
            message: 'No se pudo obtener el identificador del pago.',
            autoClose: 12000,
         });
         return false;
      }

      try {
         setIsCancelling(true);

         const url = `${process.env.REACT_APP_BACKEND_SERVER}admin/administracion/installments/cancel`
         const { data } = await axios.put(url, { payment_id, reason }, axiosConfig)

         showNotification({
            color: data.warning_message ? "yellow" : "green",
            status: "success",
            title: "Pago cancelado.",
            message: data.warning_message
               ? `${data.message} ${data.warning_message}`
               : data.message,
            autoClose: data.warning_message ? 12000 : 6000,
         });

         // Volver al listado y refrescar
         setDisplayForm(false);
         setInitialValuesEdit(null);
         setReFetch(prev => !prev);

         return true;

      } catch (error) {
         console.error(error)
         const detail =
            error.response?.data?.message ||
            ERROR_HANDLER[error.response?.status] ||
            ERROR_HANDLER.defaultError;

         showNotification({
            color: "red",
            status: "error",
            title: "Operación fallida.",
            message: `No se pudo cancelar el pago. Detalle: ${detail}`,
            autoClose: 12000,
         });
         return false;
      } finally {
         setIsCancelling(false);
      }
   }
   useEffect(() => {
      getSalesInstallments()
   }, [reFetch])

   useEffect(() => {
      if (dateFilter[0] && dateFilter[1])
         getSalesInstallments();
   }, [dateFilter]);

   const columnHelper = createColumnHelper();
   const columns = [
      columnHelper.accessor('installments_date', {
         cell: (info) => {
            const dateValue = new Date(info.getValue());
            return <div className="salesPayment-cell-center">
               {moment(dateValue).format("DD/MM/YYYY")}
            </div>;
         },
         header: 'Fecha a cobrar',
         size: 248,
         enableColumnFilter: true,
         enableSorting: false,
         filter: "date",
         filterFn: filterFunctions.dateRangeFilter,
      }),
      columnHelper.accessor('installments_state', {
         cell: (info) => {
            return getStateBadge(info.getValue());
         },
         header: 'Estado',
         size: 120,
         enableColumnFilter: true,
         enableSorting: false,
         filter: "select",
         filterOptions: [
            { value: 'PAID', label: 'Pagada' },
            { value: 'PENDING', label: 'Pendiente' },
            { value: 'EXPIRED', label: 'Vencida' },
            { value: 'CANCELLED', label: 'Cancelada' }
         ],
         filterFn: filterFunctions.selectFilter,
         tooltip: "Indica si la cuota fue pagada o no.",
      }),
      columnHelper.accessor('dummyValue', {
         cell: info => {
            return <span className="salesPayment-studentData-span"
               onClick={() => setStudentData({
                  name: info.row.original.payer_name,
                  lastname: info.row.original.payer_lastname,
                  email: info.row.original.payer_email,
                  phone: info.row.original.payer_phone,
               })}
            >
               {`${info.row.original.payer_name} ${info.row.original.payer_lastname}`}
            </span>
         },
         header: 'Cliente',
         size: 176,
         filter: "text",
         enableSorting: false,
         enableColumnFilter: true,
         filterFn: (row, id, filterValue) => {
            if (!filterValue) return true;
            return searchFlattenedObject(row.original, filterValue)
         },
      }),
      columnHelper.accessor('installments_cycle', {
         cell: (info) => `${info.row.original.installment_cycle}/${info.row.original.installments_total_cycles}`,
         header: 'Cuota',
         enableColumnFilter: false,
         enableSorting: false,
         size: 64,
      }),
      columnHelper.accessor('installments_agreedAmount', {
         cell: (info) => {
            let { payment_currency, installments_currency, installments_state } = info.row.original;
            let value = info.getValue();

            if (installments_state === 'PAID') {
               // La idea es que si esta pagada, devuelva el importe que se pagó. Verificar en el backend que estamos mandando en installments_agreedAmount
               return `$${currencyFormat(value)}`;
            }

            if (payment_currency === 'ARS' && installments_currency === 'USD') {
               value *= dollarValue; // Convert to ARS
               // The dollar value used here is today's value, and it is the same used to update /administracion/installment/:paymentId/:installmentId
            }

            return `$${currencyFormat(value)}`;
         },
         header: 'Importe',
         enableColumnFilter: false,
         enableSorting: true,
         size: 120,
         tooltip: `Importe acordado para la cuota. Los valores en ARS están sujetos a cambios de acuerdo a la cotización del dólar del día. Referencia: $${dollarValue} ARS/USD`,
      }),
      columnHelper.accessor('payment_currency', {
         cell: info => info.getValue(),
         header: 'Moneda',
         size: 88,
         enableColumnFilter: true,
         enableSorting: false,
         filter: "select",
         filterOptions: [
            { value: 'ARS', label: 'ARS' },
            { value: 'USD', label: 'USD' },
         ],
         filterFn: (row, id, filterValue) => {
            if (!filterValue) return true;
            return row.original.payment_currency.toLowerCase() === filterValue.toLowerCase();
         }
      }),
      columnHelper.accessor('hasPendingInstallments', {
         header: 'Tiene previas',
         tooltip: "Está evaluando las cuotas previas al rango de fecha seleccionado. No tiene en cuenta las que correspondan a la fecha marcada.",
         size: 112,
         enableSorting: false,
         enableColumnFilter: true,
         filterOptions: ['Si', 'No'],
         filter: "select",
         cell: (info) => (
            <Badge color={info.getValue() ? "var(--color-badge-green)" : "var(--color-badge-red)"}>
               {info.getValue() ? 'Si' : 'No'}
            </Badge>
         ),


         filterFn: (row, columnId, filterValue) => {
            if (!filterValue) return true;

            const rowValue = row.getValue(columnId);

            // Mapeamos el string del select al booleano real
            const mappedFilterValue = filterValue === 'Si';

            return rowValue === mappedFilterValue;
         },
      }),
      columnHelper.accessor('platform', {
         cell: (info) => {
            return <div className="salesPayment-cell-center">
               {getChargeType(info.getValue())}
            </div>;
         },
         header: 'Tipo cobro',
         enableColumnFilter: true,
         enableSorting: false,
         size: 112,
         filter: "select",
         filterOptions: [
            { value: 'manual', label: 'MANUAL' },
            { value: 'automatic', label: 'AUTOMÁTICO' },
         ],
         filterFn: (row, id, filterValue) => {
            if (!filterValue) return true;
            return (chargeTypes[filterValue].includes(row.original.platform.toUpperCase()))
         },
      }),
      columnHelper.accessor('bankTransferAccount', {
         cell: (info) => {
            const bankAccountId = info.getValue()
            const current_bank_account = bankAccountsOptions?.find(item => item.value === bankAccountId)
            if (!current_bank_account) return "Sin identificar"
            return <div className="salesPayment-cell-center">
               {current_bank_account.label}
            </div>;
         },
         header: 'Cuenta bancaria',
         enableColumnFilter: true,
         enableSorting: false,
         size: 300,
         filter: "select",
         filterOptions: bankAccountsOptions,
         filterFn: filterFunctions.selectFilter
      }),

      columnHelper.accessor('academicUnit', {
         cell: info => info.getValue(),
         header: 'Unidad académica',
         size: 400,
         enableColumnFilter: false,
         enableSorting: false,
      }),
   ]



   // TABLE FUNCTIONS
   const putInstallments = async (values) => {
      const putURL = `${process.env.REACT_APP_BACKEND_SERVER}admin/administracion/installments/${values.payment_id}`
      const jsonData = { data: values }
      return axios.put(putURL, jsonData, axiosConfig)
   }

   const editInstallments = (rowData) => {
      if (rowData) {
         setInitialValuesEdit(rowData)
         setDisplayForm(true)
      }
   }

   const handleSubmitForm = async (values) => {
      try {
         setIsLoading(true)

         await putInstallments(values)

         showNotification({
            color: "green",
            status: "success",
            title: "Cuotas actualizadas correctamente.",
            message: `Detalle: las cuotas se han guardado correctamente.`,
         });

         setReFetch(!reFetch)
         setDisplayForm(false)
      } catch (err) {
         showNotification({
            color: "red",
            status: "error",
            title: "Error al editar cuotas.",
            message: `Detalle: ${ERROR_HANDLER[err.request?.status] || ERROR_HANDLER.defaultError}`,
            autoClose: 12000
         });
      } finally {
         setIsLoading(false)
      }
   }


   const editPaymentStateAction = (rowData) => {
      const isActionInProgress = isSwitchLoading === rowData.installment_id;
      const isPayed = getIfPayed(rowData.installments_state);
      const isCancelled = rowData.installments_state === 'CANCELLED';
      if (isActionInProgress)
         return <Loader size={20} />;



      return (
         <Flex>
            {!isPayed && !isCancelled && <Tooltip label="Marcar como pagado" color="rgba(54,54,54,1)">
               <IconSquareCheck
                  className="salesPayment-confirmation-icon"
                  onClick={() => openConfirmModal(rowData)}
               />
            </Tooltip>}
            <IconEye onClick={() => setDetailsModal(rowData?.allInstallmentsInfo || false)} />
         </Flex>
      );
   };

   const getIfPayed = (value) => {
      switch (value?.toLowerCase()) {
         case 'pending':
            return false;
         case 'success':
            return true;
         case 'paid':
            return true;
         default:
            return null;
      }
   }

   const openConfirmModal = (_installment) => {
      if (_installment) {
         return setIsConfirmModalData(_installment)
      }
      return setIsConfirmModalData(null)

   }

   const onChangeStateInstallmentsSwitch = async (_installment) => {
      const installmentId = _installment.installment_id;
      const paymentId = _installment.payment_id
      if (!paymentId) {
         return showNotification({
            color: "red",
            title: "Ha ocurrido un error",
            message: "No se ha encontrado el identificador de la cuota"
         })
      }
      try {
         setIsSwitchLoading(installmentId);

         if (_installment.cycle_receipt && isBase64(_installment.cycle_receipt)) {
            const clearTitle = `${_installment.payer_email}-cycle-${_installment.installment_cycle}`.replace(/[^\w\s]/g, "").trim();
            _installment.cycle_receipt = await uploadToS3({
               image: _installment.cycle_receipt,
               fileName: `${clearTitle}-${moment().format("DD-MM-YYYY")}`,
               fileDirectory: "receipts",
               isRequired: true,
            });
         }


         const putURL = `${process.env.REACT_APP_BACKEND_SERVER}admin/administracion/installment/${paymentId}/${installmentId}`
         await axios.put(
            putURL,
            { data: { dollarValue: dollarValue, cycle_receipt: _installment?.cycle_receipt || "", new_payment_date: _installment.new_payment_date } },
            axiosConfig);

         showNotification({
            color: "green",
            status: "success",
            title: "¡Operación exitosa!",
            message: "El estado del cobro ha sido modificado.",
            autoClose: 5000,
         });

         setIsConfirmModalData(null)
         setReFetch(!reFetch);

      } catch (error) {
         showNotification({
            color: "red",
            status: "error",
            title: "Operación fallida.",
            message: `No se pudo actualizar el estado del cobro. Detalle: ${error.message || ERROR_HANDLER[error.request?.status] || ERROR_HANDLER.defaultError
               }`,
            autoClose: 12000,
         });
      } finally {
         setIsSwitchLoading(null);
      }
   }

   return {
      data,
      columns,
      isLoading,
      studentData,
      setStudentData,
      editInstallments,
      displayForm,
      setDisplayForm,
      initialValuesEdit,
      setInitialValuesEdit,
      editPaymentStateAction,
      handleSubmitForm,
      setDateFilter,
      dateFilter,
      onChangeStateInstallmentsSwitch,
      confirmModalData,
      onCloseConfirmModal: () => setIsConfirmModalData(null),
      isSwitchLoading,
      detailsModal,
      onCloseDetailsModal: () => setDetailsModal(false),
      formatDate: (date) => {
         return moment(date).format('YYYY-MM-DD');
      },
      cancelPayment,
      isCancelling
   }
}
